import { FC, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Grid, TextInput } from 'portal-commons';
import { RcsCampaign } from '../../types';
import {
  createEmailValidator,
  createMaxLengthValidator,
  isRequired,
} from '../../validators';

interface TechnicalContactFormProps {
  campaign: RcsCampaign;
}

interface TechnicalContactFormValues {
  technicalContactName: string;
  technicalContactEmail: string;
  technicalContactJobTitle: string;
}

const TechnicalContactForm: FC<TechnicalContactFormProps> = ({ campaign }) => {
  const { control, clearErrors, reset } =
    useFormContext<TechnicalContactFormValues>();

  const defaultValues = useMemo(() => {
    return {
      technicalContactName: campaign.technicalContactName || '',
      technicalContactJobTitle: campaign.technicalContactJobTitle || '',
      technicalContactEmail: campaign.technicalContactEmail || '',
    };
  }, [campaign]);

  const {
    field: { value: name, onChange: setName },
    fieldState: { error: nameError },
  } = useController({
    control,
    name: 'technicalContactName',
    defaultValue: campaign.technicalContactName || '',
    rules: {
      validate: {
        isRequired,
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });

  const {
    field: { value: jobTitle, onChange: setJobTitle },
    fieldState: { error: jobTitleError },
  } = useController({
    control,
    name: 'technicalContactJobTitle',
    defaultValue: campaign.technicalContactJobTitle || '',
    rules: {
      validate: {
        isRequired,
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });

  const {
    field: { value: email, onChange: setEmail },
    fieldState: { error: emailError },
  } = useController({
    control,
    name: 'technicalContactEmail',
    defaultValue: campaign.technicalContactEmail || '',
    rules: {
      validate: {
        isRequired,
        withValidInput: createEmailValidator(),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  useEffect(() => {
    clearErrors();
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Grid
      sx={{
        gridTemplateColumns: '1fr 1fr',
        columnGap: 'l',
        rowGap: 'xl',
      }}
    >
      <TextInput
        label="Name"
        value={name}
        onChange={setName}
        error={!!nameError?.message}
        note={nameError?.message}
        data-testid="technicalContactName"
      />
      <TextInput
        label="Job Title"
        value={jobTitle}
        onChange={setJobTitle}
        error={!!jobTitleError?.message}
        note={jobTitleError?.message}
        data-testid="technicalContactJobTitle"
      />
      <TextInput
        label="Email"
        value={email}
        onChange={setEmail}
        error={!!emailError?.message}
        note={emailError?.message}
        data-testid="technicalContactEmail"
      />
    </Grid>
  );
};

export default TechnicalContactForm;
